import { Button } from '@/components/ui/button';
import { useChatStore } from '@/stores/chatStore';
import { getGuidedFlow } from '@/services/ai/guidedFlows';
import { ArrowRight, X } from 'lucide-react';

export default function GuidedFlowRenderer() {
  const activeGuidedFlow = useChatStore(s => s.activeGuidedFlow);
  const stepIndex = useChatStore(s => s.activeGuidedFlowStepIndex);
  const cancelGuidedFlow = useChatStore(s => s.cancelGuidedFlow);
  const sendMessage = useChatStore(s => s.sendMessage);
  const isStreaming = useChatStore(s => s.isStreaming);

  if (!activeGuidedFlow) return null;

  const flow = getGuidedFlow(activeGuidedFlow);
  if (!flow) return null;

  const step = flow.steps[stepIndex];
  if (!step) return null;

  const handleChoice = (choice: string) => {
    const nextStepId = step.nextStepFn(choice);

    if (nextStepId) {
      const nextIndex = flow.steps.findIndex(s => s.id === nextStepId);
      if (nextIndex >= 0) {
        useChatStore.setState({ activeGuidedFlowStepIndex: nextIndex });
        sendMessage(choice);
        return;
      }
    }

    // Flow complete
    sendMessage(choice);
    cancelGuidedFlow();
  };

  return (
    <div className="mx-4 mb-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <ArrowRight className="w-3.5 h-3.5 text-blue-600" />
          <span className="text-xs font-medium text-blue-700">{flow.title}</span>
          <span className="text-xs text-blue-500">
            Step {stepIndex + 1}/{flow.steps.length}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={cancelGuidedFlow}
          className="h-6 w-6 p-0 text-blue-400 hover:text-blue-600"
        >
          <X className="w-3.5 h-3.5" />
        </Button>
      </div>

      {step.choices && step.choices.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {step.choices.map((choice) => (
            <Button
              key={choice}
              variant="outline"
              size="sm"
              disabled={isStreaming}
              onClick={() => handleChoice(choice)}
              className="h-auto py-1 px-2.5 text-xs bg-white hover:bg-blue-100 hover:border-blue-400"
            >
              {choice}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
